import React from 'react';
import { NetworkStatus } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import Directory from './Directory';
import withLayout from 'hocs/withLayout';
import { PageProps } from 'types/page';
import { USER_DIRECTORY_USERS } from 'lib/graphql/queries/user';
import { CREDIT_TYPES } from 'lib/graphql/queries/creditTypes';
import { CreativeDirectory } from 'types/creativeDirectory';
import { reportError } from 'lib/errors';

export type UserDirectoryVariables = {
  creditTypes?: Array<string>;
  searchInput?: string;
};

type CreditTypesBootstrapData = {
  creditTypes: Array<{ typeId: string; typeName: string; typeDescription?: string }>;
};

type UserDirectoryUsersData = {
  userDirectory: CreativeDirectory['userDirectory'];
};

/** Directory filter chips: Artist, Director, Editor, Producer (matched on `typeName`). */
const DIRECTORY_FILTER_ROLE_NAMES = new Set(['artist', 'director', 'editor', 'producer']);

export function isDirectoryFilterRole(typeName: string): boolean {
  return DIRECTORY_FILTER_ROLE_NAMES.has(typeName.trim().toLowerCase());
}

export function buildDirectoryQueryVariables(
  activeLabels: string[],
  directoryFilterTypeIds: string[],
  debouncedSearch: string,
): UserDirectoryVariables {
  const ids = activeLabels.length > 0 ? activeLabels : directoryFilterTypeIds;
  return {
    creditTypes: ids.length > 0 ? ids : [],
    searchInput: debouncedSearch || undefined,
  };
}

/** Same variables as every directory chip selected (Artist–Producer only). */
export function getVariablesAsAllChipsSelected(
  directoryFilterTypeIds: string[],
  debouncedSearch: string,
): UserDirectoryVariables {
  return {
    creditTypes: directoryFilterTypeIds.length > 0 ? [...directoryFilterTypeIds] : [],
    searchInput: debouncedSearch || undefined,
  };
}

const SEARCH_DEBOUNCE_MS = 300;

const Index = (props: DirectoryIndexPropTypes) => {
  const [searchInput, setSearchInput] = React.useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = React.useState<string>('');
  const [activeLabels, setActiveLabels] = React.useState<string[]>([]);
  /** Must become true in the same tick as `setActiveLabels` so `useQuery` never runs with empty labels while credit types are ready. */
  const [directoryLabelsSeeded, setDirectoryLabelsSeeded] = React.useState(false);

  React.useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchInput]);

  const { data: creditTypesBootstrap, loading: creditTypesLoading } =
    useQuery<CreditTypesBootstrapData>(CREDIT_TYPES, {
      fetchPolicy: 'network-only',
    });

  const directoryCreditTypes = React.useMemo(
    () =>
      (creditTypesBootstrap?.creditTypes ?? []).filter((t) =>
        isDirectoryFilterRole(t.typeName),
      ),
    [creditTypesBootstrap?.creditTypes],
  );

  const directoryTypeIds = React.useMemo(
    () => directoryCreditTypes.map((t) => t.typeId),
    [directoryCreditTypes],
  );

  const creditTypesReady =
    !creditTypesLoading && creditTypesBootstrap?.creditTypes != null;

  /**
   * Seed chip state to match Artist–Producer once `CREDIT_TYPES` is available.
   * Adjust during render so `useQuery` is not skipped before labels exist.
   * If `activeLabels` is already non-empty (e.g. chip click before seed), do not overwrite.
   */
  if (creditTypesReady && !directoryLabelsSeeded) {
    if (activeLabels.length === 0) {
      setActiveLabels([...directoryTypeIds]);
    }
    setDirectoryLabelsSeeded(true);
  }

  const directoryVariables = React.useMemo(
    () => buildDirectoryQueryVariables(activeLabels, directoryTypeIds, debouncedSearch),
    [activeLabels, directoryTypeIds, debouncedSearch],
  );

  const skipUserDirectory = !creditTypesReady || !directoryLabelsSeeded;

  const {
    data: dirData,
    previousData: dirPrevious,
    loading: directoryApolloLoading,
    error: directoryGraphqlError,
    networkStatus,
    refetch: refetchUserDirectory,
  } = useQuery<UserDirectoryUsersData, UserDirectoryVariables>(USER_DIRECTORY_USERS, {
    variables: directoryVariables,
    skip: skipUserDirectory,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const variables = directoryVariables;

  const refetchDirectory = React.useCallback(
    (next?: UserDirectoryVariables) => {
      const v = next ?? variables;
      return refetchUserDirectory(v);
    },
    [refetchUserDirectory, variables],
  );

  const mergedDirectory = React.useMemo((): CreativeDirectory | undefined => {
    const list = dirData?.userDirectory ?? dirPrevious?.userDirectory ?? undefined;
    if (list == null || !Array.isArray(list)) {
      return undefined;
    }
    const roles = directoryCreditTypes;
    return {
      userDirectory: list,
      creditTypes: roles.map((c) => ({
        ...c,
        typeDescription: c.typeDescription ?? '',
      })),
    };
  }, [dirData?.userDirectory, dirPrevious?.userDirectory, directoryCreditTypes]);

  const hasDirectoryPayload =
    mergedDirectory != null && Array.isArray(mergedDirectory.userDirectory);

  const directoryNetworkBusy =
    !skipUserDirectory &&
    (directoryApolloLoading ||
      networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.setVariables ||
      networkStatus === NetworkStatus.refetch);

  const queryInFlight =
    creditTypesLoading ||
    !directoryLabelsSeeded ||
    (!hasDirectoryPayload && directoryNetworkBusy);

  if (directoryGraphqlError) {
    reportError(directoryGraphqlError, {
      metaData: {
        operation: 'graphql query userDirectoryUsers',
        mode: 'Directory index',
      },
      severity: 'warning',
    });
  }

  return (
    <Directory
      {...props}
      data={mergedDirectory}
      loading={false}
      queryLoading={queryInFlight}
      error={undefined}
      refetch={refetchDirectory}
      searchInput={searchInput}
      setSearchInput={setSearchInput}
      activeLabels={activeLabels}
      setActiveLabels={setActiveLabels}
      variables={variables}
      filterCreditTypes={directoryCreditTypes}
    />
  );
};

export type DirectoryIndexPropTypes = PageProps;

export default withLayout(Index, { contentWidth: 'wide' });
